// V2 Business Partners roster — replaces the V1 desktop-table / mobile-card split
// with full-width V2 roster rows (avatar, name + badges, contact line, deal stat,
// action cluster) and per-type empty states.
//
// Modal state (handleOpenModal, handleCloseModal, handleSave, handleDelete) and
// the modalTrigger effect are preserved verbatim from the V1 implementation.

import { formatPhoneDisplay } from '../../../utils/phoneNumbers';
import React from 'react';
import VendorFormModal from './VendorFormModal';
import { Badge } from '../../ui/primitives';
import EmptyStateCTA from '../../ui/EmptyStateCTA';
import { useTour } from '../tours/TourContext';

// Derive up-to-2-char initials from a company name (or first/last fallback)
function getInitials(item) {
    const company = (item.company || '').trim();
    if (company) {
        const words = company.split(/\s+/).filter(Boolean);
        if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
        return (words[0][0] + words[1][0]).toUpperCase();
    }
    const first = (item.firstName || '').trim();
    const last  = (item.lastName  || '').trim();
    if (first && last)  return (first[0] + last[0]).toUpperCase();
    if (first)          return first.slice(0, 2).toUpperCase();
    if (last)           return last.slice(0, 2).toUpperCase();
    return '?';
}

function VendorListPage({
    title,
    items            = [],
    allItems         = items,   // full unfiltered list (for empty-state detection)
    onAdd,
    onUpdate,
    onDelete,
    modalTrigger     = 0,
    entityLabel      = title,
    linkedContactsConfig = null,
    companiesConfig      = null,
    googleContactsConfig = null,
    hideHeader           = false,
    dealCounts           = new Map(),
    searchQuery          = '',
}) {
    const { launchTour } = useTour();

    const [isModalOpen,  setIsModalOpen]  = React.useState(false);
    const [currentItem,  setCurrentItem]  = React.useState(null);
    const previousModalTriggerRef         = React.useRef(modalTrigger);
    const childFieldName = linkedContactsConfig?.fieldName || null;

    const handleOpenModal = (item = null) => {
        setCurrentItem(item);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setCurrentItem(null);
    };

    const handleSave = async (itemData) => {
        // Await the write so the modal can surface failures (it `await`s onSave)
        // and close exactly once via its own onClose — not here (avoids a
        // double-close race + premature success toast).
        if (currentItem && currentItem.id) {
            await onUpdate(currentItem.id, itemData);
        } else {
            await onAdd(itemData);
        }
    };

    const handleDelete = async () => {
        if (!currentItem?.id || typeof onDelete !== 'function') return;
        const wasDeleted = await onDelete(currentItem.id);
        if (wasDeleted) {
            handleCloseModal();
        }
    };

    // Preserve modalTrigger effect exactly (opens new-item modal from CTA)
    React.useEffect(() => {
        if (modalTrigger !== previousModalTriggerRef.current) {
            setCurrentItem(null);
            setIsModalOpen(true);
        }
        previousModalTriggerRef.current = modalTrigger;
    }, [modalTrigger]);

    // Derive display labels from title/entityLabel
    const pluralLabel    = title ? title.toLowerCase() : `${entityLabel.toLowerCase()}s`;

    // Determine empty-state type: true empty (no items at all) vs. no-results (search filtered)
    const hasNoItemsAtAll    = allItems.length === 0;
    const hasNoSearchResults = !hasNoItemsAtAll && items.length === 0 && searchQuery;

    return (
        <div>
            {/* V1 hideHeader preserved — page shell owns heading now.
                The partners-header/partners-add data-tour anchors that used to live in this
                block were moved to BusinessPartnersPage.js (its <header> and Add <Button>):
                this block never renders at its one call site (BusinessPartnersPage.js always
                passes hideHeader), so partnersPageTour.js's first two steps had nothing to
                highlight in the shipped app. See 46-04-SUMMARY.md. */}
            {!hideHeader && (
                <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="text-2xl font-bold text-[color:var(--text-primary)]">{title}</h2>
                    <button
                        type="button"
                        onClick={() => handleOpenModal()}
                        className="inline-flex items-center justify-center rounded-md bg-[color:var(--color-accent)] px-4 py-2 text-sm font-semibold text-[color:var(--on-accent)] transition-opacity hover:opacity-90"
                    >
                        {`Add ${entityLabel}`}
                    </button>
                </div>
            )}

            {/* Empty state: tab has 0 partners */}
            {hasNoItemsAtAll && (
                <EmptyStateCTA
                    pageSlug="partners"
                    heading="No partners yet"
                    body="Add vendors, lenders, and other partners you work with on deals."
                    onTakeTour={() => launchTour('partners-page')}
                    primaryActionLabel="Add partner"
                    onPrimaryAction={() => handleOpenModal()}
                />
            )}

            {/* No-results state: search returned nothing */}
            {hasNoSearchResults && (
                <p style={{ color: 'var(--text-muted)', padding: '24px 0', textAlign: 'center', fontSize: 14 }}>
                    No {pluralLabel} match your search.
                </p>
            )}

            {/* V2 roster rows */}
            {items.length > 0 && (
                <div data-tour="partners-list" className="bp-roster" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {items.map((item, index) => {
                        const initials = getInitials(item);
                        const displayName = (item.company || '').trim()
                            || `${item.firstName || ''} ${item.lastName || ''}`.trim()
                            || 'Unnamed';
                        const contactParts = [
                            `${item.firstName || ''} ${item.lastName || ''}`.trim(),
                            item.email,
                            formatPhoneDisplay(item.phone),
                        ].filter(Boolean);
                        const contactLine  = contactParts.join(' · ');
                        const dealCount    = dealCounts.get(item.id) || 0;

                        return (
                            <article
                                key={item.id}
                                className="bg-[color:var(--surface-primary)] border border-[color:var(--border-color)] rounded-xl transition-all duration-150 ease-rise hover:border-[color:var(--color-primary)] hover:shadow-[0_10px_28px_rgba(0,0,0,0.13)] hover:-translate-y-0.5 bp-row"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '46px minmax(0,1fr) auto',
                                    gap: 16,
                                    padding: '16px 18px',
                                    cursor: 'pointer',
                                    alignItems: 'center',
                                    animationDelay: `${0.30 + index * 0.04}s`,
                                }}
                                onClick={() => handleOpenModal(item)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleOpenModal(item);
                                    }
                                }}
                                aria-label={`Edit ${displayName}`}
                            >
                                {/* 1. Avatar — gradient, mirrors ClientCardV2 */}
                                <div
                                    className="flex items-center justify-center"
                                    aria-hidden="true"
                                    style={{
                                        width: 46, height: 46, flexShrink: 0,
                                        borderRadius: 13,
                                        background: 'linear-gradient(140deg, rgba(20,81,61,0.16), rgba(196,154,76,0.18))',
                                    }}
                                >
                                    <span
                                        style={{
                                            fontSize: 17, fontWeight: 600,
                                            lineHeight: 1,
                                            color: 'var(--color-primary)',
                                            fontFamily: 'var(--font-sans)',
                                        }}
                                    >
                                        {initials}
                                    </span>
                                </div>

                                {/* 2. Name + badges + contact line */}
                                <div className="min-w-0">
                                    <div
                                        className="bp-row-name"
                                        style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}
                                    >
                                        <span
                                            style={{
                                                fontSize: 17, fontWeight: 600,
                                                letterSpacing: '-0.01em',
                                                color: 'var(--text-primary)', lineHeight: 1,
                                            }}
                                        >
                                            {displayName}
                                        </span>
                                        {/* Realtor-only: linked-contacts badge (D-05) */}
                                        {linkedContactsConfig && childFieldName &&
                                            Array.isArray(item[childFieldName]) &&
                                            item[childFieldName].length > 0 && (
                                            <Badge tone="accent" size="sm">
                                                {item[childFieldName].length} linked
                                            </Badge>
                                        )}
                                    </div>
                                    {contactLine && (
                                        <div
                                            className="bp-row-sub"
                                            style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 3 }}
                                        >
                                            {contactLine}
                                        </div>
                                    )}
                                </div>

                                {/* 3. Deal stat — mirrors ClientCardV2 stat column */}
                                <div
                                    className="bp-row-stat flex-shrink-0"
                                    style={{ textAlign: 'right', paddingRight: 2 }}
                                >
                                    <div
                                        className="text-[17px] font-semibold tabular-nums tracking-[-0.02em]"
                                        style={{ lineHeight: 1, color: 'var(--text-primary)' }}
                                    >
                                        {dealCount}
                                    </div>
                                    <div
                                        className="text-[11px] uppercase tracking-[0.06em] text-[color:var(--text-muted)]"
                                        style={{ marginTop: 3 }}
                                    >
                                        Deals
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {/* VendorFormModal — props unchanged from V1 */}
            {isModalOpen && (
                <VendorFormModal
                    initialData={currentItem}
                    title={title}
                    entityLabel={entityLabel}
                    onClose={handleCloseModal}
                    onSave={handleSave}
                    onDelete={typeof onDelete === 'function' ? handleDelete : undefined}
                    linkedContactsConfig={linkedContactsConfig}
                    companiesConfig={companiesConfig}
                    googleContactsConfig={googleContactsConfig}
                />
            )}
        </div>
    );
}

export default VendorListPage;
